/** @jsxImportSource @emotion/react */
import { Box, Button, Grid, Link as MaterialLink, Theme, Typography, useTheme } from '@mui/material';
import Heading from 'components/Heading';
import CommonPageLayout from 'components/layouts/CommonPageLayout';
import NewsItem from 'components/NewsItem';
import Proposals from 'components/Proposals';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { UseStyles } from 'types/styles';
import { asset } from 'utils/asset';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { usePaths } from 'hooks/usePaths';
import { Link } from 'react-router-dom';

const HomePage: React.FC = () => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const { formatMessage } = useIntl();
  const paths = usePaths();

  return (
    <CommonPageLayout>
      <Helmet>
        <title>{formatMessage({ id: 'pageTitle.home', defaultMessage: 'Homepage' })}</title>
      </Helmet>
      <img
        css={classes.hero}
        src={asset('/images/home/hero.jpg')}
        srcSet={`${asset('/images/home/hero@2x.jpg')} 2x`}
        alt=""
        width="100%"
      />
      <Box p={8}>
        <Typography m={2} mb={10} component="p" variant="subtitle2" color={theme.palette.custom.primary.brand[150]}>
          <FormattedMessage
            id={'HomePage.welcome'}
            defaultMessage={
              'Welcome to the WHO Partners Platform microsite which supports the development of national plans and proposals aimed at strengthening health emergency preparedness, readiness, and resilience. The tools and templates available on this platform, along with instructions on how to use them to support the development of national plans and proposals, are explained in the help section. Additionally, they were also outlined in a webinar series that was first broadcast in March 2023 and is available on'
            }
          />{' '}
          <MaterialLink
            href="https://openwho.org/courses/webinar-pandemic-fund-proposals"
            target="_blank"
            rel="noreferrer"
          >
            OpenWHO
          </MaterialLink>
          .
        </Typography>
        <Box m={2} mt={8}>
          <Proposals />
          <Box mt={4}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ whiteSpace: 'nowrap' }}
              endIcon={<ArrowForwardIcon />}
              component={Link}
              to={paths.templates.generate()}
            >
              Download templates
            </Button>
          </Box>
        </Box>
        <Heading mt={10} mb={5}>
          <FormattedMessage id={'HomePage.news'} defaultMessage={'News'} />
        </Heading>
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={6} lg={3}>
            <NewsItem
              title="Countries set out way forward for continued negotiations on global agreement on pandemic prevention, preparedness, and response"
              url="https://www.who.int/news/item/17-06-2023-countries-set-out-way-forward-for-continued-negotiations-on-global-agreement-on-pandemic-prevention--preparedness--and-response"
              date="17 June 2023"
              img="https://www.who.int/images/default-source/departments/department-of-communications/inb5-group-discussion.tmb-479v.jpg?Culture=en&sfvrsn=a62182cd_1"
            />
          </Grid>
          {/* <Grid item xs={12} md={6} lg={3}>
            <NewsItem
              title="Training to build community capacity to meaningfully engage in mpox response"
              url="https://www.who.int/news/item/13-06-2023-training-to-build-community-capacity-to-meaningfully-engage-in-mpox-response"
              date="13 June 2023"
              img="https://cdn.who.int/media/images/default-source/emergencies/risk-communication/who-emro-training-participants.tmb-479v.jpg?sfvrsn=d2e3f36_1"
            />
          </Grid> */}
          <Grid item xs={12} md={6} lg={3}>
            <NewsItem
              title="Marburg virus disease outbreak in Equatorial Guinea"
              url="https://www.who.int/news/item/08-06-2023-marburg-virus-disease-outbreak-in-equatorial-guinea"
              date="8 June 2023"
              img="https://cdn.who.int/media/images/default-source/emergencies/2023-04-17-17-13-20---copy.tmb-479v.jpeg?sfvrsn=8c44ccb7_1"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <NewsItem
              title="Developing a global research agenda for public health and social measures: Research priorities for COVID-19"
              url="https://www.who.int/news/item/05-06-2023-developing-a-global-research-agenda-for-public-health-and-social-measures-research-priorities-for-covid-19"
              date="5 June 2023"
              img="https://www.who.int/images/default-source/departments/epi-win/phsm/phsm1.tmb-479v.png?Culture=en&sfvrsn=6192eca3_3"
            />
          </Grid>
        </Grid>
      </Box>
    </CommonPageLayout>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  hero: {
    display: 'block',
    maxHeight: 360,
    objectFit: 'cover',
  },
});

export default HomePage;
